/**
 * @file AgencyPricingNote.jsx
 * @description Shared pricing copy for agency marketing pages (plans from Jan 2027). While the free period runs it
 *   says the first N openings are free through the end date (no card) and that the new plans start the next day;
 *   after it only the plans remain. Starter, Pro, extra-opening prices and included openings come from
 *   getAgencyPricing (platform_config). ABAcases Workforce is named only while its release switch is on
 *   (agencyPricing.workforceIncluded). Never implies posting is free forever. Copy: agencyPricing.* locale keys.
 * @module components/AgencyPricingNote
 */
import { useTranslation } from "react-i18next";
import { useConfigVersion } from "../lib/useConfigVersion";
import { getAgencyPricing } from "../lib/agencyPricing";

/** The sentences, in order, for the current state. Pure, so tests and other surfaces can reuse it. */
export function agencyPricingSentences(t, p) {
  const plans = [
    t("agencyPricing.starterLine", { price: p.starterMo, count: p.starterIncluded }),
    t("agencyPricing.proLine", { price: p.proMo, annual: p.proAnnualMo, count: p.proCases, teamLogins: p.teamLogins }),
    t("agencyPricing.extrasLine", { starter: p.starterExtra, pro: p.proExtra }),
    ...(p.workforceIncluded ? [t("agencyPricing.workforceLine", { starter: p.workforceMaxStarter, pro: p.workforceMaxPro })] : []),
  ];
  if (!p.isPromoActive) return plans;
  return [
    t("agencyPricing.freePeriodLine", { count: p.promoCount, endDate: p.endDate }),
    `${t("agencyPricing.fromDate", { startDate: p.startDate })} ${plans[0]}`,
    ...plans.slice(1),
  ];
}

export default function AgencyPricingNote({ style }) {
  useConfigVersion(); // re-render with the Admin prices once platform_config loads
  const { t, i18n } = useTranslation();
  const p = getAgencyPricing(i18n.language);
  return (
    <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0, ...style }}>
      {agencyPricingSentences(t, p).join(" ")}
    </p>
  );
}
